import { useEffect, useState } from 'react';
import { HandCoins, CalendarPlus, X, Landmark } from 'lucide-react';
import { doc, onSnapshot } from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { db } from '../firebase';

// "Affiliate Program" — everyone is in automatically (no opt-in). The modal
// always opens on a reward picker: free months (two months of Gnosis per
// subscriber brought in — no Stripe needed) or cash back (half the net income
// from each referred subscription for 12 months, paid monthly via Stripe
// Connect). Each choice shows its own link — the reward is baked into the
// URL (&reward=), so whichever deal a shared link advertised is the deal
// that referral pays out, forever. The FREE90 promo code is deliberately not
// surfaced here — it drops the paywall entirely and those users aren't set
// up to be closed.

export type AffiliateStage = 'locked' | 'connected';
export type RewardMode = 'months' | 'cash';

/** Live affiliate doc (creators/{uid}) maintained by the Cloud Functions. */
interface CreatorDoc {
  signups?: number;
  subscribed?: number;
  pendingCents?: number;
  paidOutCents?: number;
  monthsEarned?: number;
  connected?: boolean;
}

export interface AffiliatePreview {
  stage?: AffiliateStage;
  /** 'choice' renders the pick-a-reward screen; defaults to 'cash'. */
  mode?: 'choice' | RewardMode;
  stats?: { signups: number; paid: number; pendingCents: number; paidOutCents: number; monthsEarned?: number };
}

interface ReferralPanelProps {
  user: User;
  /** Gallery/dev: render the modal body inline, forced to a given state. */
  preview?: AffiliatePreview;
}

const money = (cents: number) => `$${Math.round(cents / 100)}`;

async function callFn(name: string, data?: unknown) {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { getApp } = await import('firebase/app');
  return httpsCallable(getFunctions(getApp()), name)(data);
}

function CopyRow({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button type="button" className="affil-copy-row" onClick={copy} aria-label={`Copy ${label}`}>
      <span className="affil-copy-value">{value}</span>
      <span className="affil-copy-hint">{copied ? 'Copied!' : 'Copy'}</span>
    </button>
  );
}

function AffiliateBody({ user, preview }: ReferralPanelProps) {
  const link = (reward: RewardMode) =>
    `${window.location.origin}${import.meta.env.BASE_URL}?ref=${user.uid}&reward=${reward}`;

  const [live, setLive] = useState<CreatorDoc>({});
  useEffect(() => {
    if (preview) return;
    return onSnapshot(doc(db, 'creators', user.uid),
      (snap) => setLive(snap.exists() ? (snap.data() as CreatorDoc) : {}));
  }, [user.uid, preview]);

  // Re-check Connect status on open so the modal flips to "connected" right
  // after the user returns from Stripe onboarding (fire-and-forget).
  useEffect(() => {
    if (preview) return;
    callFn('affiliateStatus').catch(() => { /* snapshot renders last-known state */ });
  }, [preview]);

  const [connecting, setConnecting] = useState(false);
  const connect = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const res = await callFn('affiliateOnboard', { origin: window.location.origin });
      const url = (res.data as { url?: string })?.url;
      if (url) { window.location.href = url; return; }
      setConnecting(false);
    } catch {
      setConnecting(false);
    }
  };

  // Pure view navigation — the modal always opens on the picker; the reward
  // itself lives in each view's link, not in stored state.
  const [view, setView] = useState<'choice' | RewardMode>(preview ? (preview.mode ?? 'cash') : 'choice');

  const stage: AffiliateStage = preview?.stage ?? (live.connected ? 'connected' : 'locked');
  const stats = preview?.stats ?? {
    signups: live.signups ?? 0,
    paid: live.subscribed ?? 0,
    pendingCents: live.pendingCents ?? 0,
    paidOutCents: live.paidOutCents ?? 0,
    monthsEarned: live.monthsEarned ?? 0,
  };

  if (view === 'choice') {
    return (
      <>
        <HandCoins size={30} className="referral-modal-icon" />
        <div className="referral-modal-title">Affiliate Program</div>
        <p className="referral-modal-sub">
          How do you want to earn?
        </p>
        <button className="affil-choice" onClick={() => setView('months')}>
          <CalendarPlus size={20} className="affil-choice-icon" />
          <span className="affil-choice-text">
            <span className="affil-choice-head">Get Free Months</span>
            <span className="affil-choice-desc">Two free months of Gnosis for every subscriber you bring in.</span>
          </span>
        </button>
        <button className="affil-choice" onClick={() => setView('cash')}>
          <Landmark size={20} className="affil-choice-icon" />
          <span className="affil-choice-text">
            <span className="affil-choice-head">Get Cash Back</span>
            <span className="affil-choice-desc">Earn half of the subscription income from every recruit, for a whole year.</span>
          </span>
        </button>
      </>
    );
  }

  if (view === 'months') {
    return (
      <>
        <HandCoins size={30} className="referral-modal-icon" />
        <div className="referral-modal-title">Affiliate Program</div>
        <p className="referral-modal-sub">
          Get two free months of Gnosis for every subscriber you bring in.
        </p>
        <CopyRow value={link('months')} label="referral link" />
        <div className="affil-stats affil-stats--3">
          <div className="affil-stat"><span className="affil-stat-num">{stats.signups}</span><span className="affil-stat-label">Sign-ups</span></div>
          <div className="affil-stat"><span className="affil-stat-num">{stats.paid}</span><span className="affil-stat-label">Subscribed</span></div>
          <div className="affil-stat"><span className="affil-stat-num">{stats.monthsEarned ?? 0}</span><span className="affil-stat-label">Months earned</span></div>
        </div>
      </>
    );
  }

  return (
    <>
      <HandCoins size={30} className="referral-modal-icon" />
      <div className="referral-modal-title">Affiliate Program</div>
      <p className="referral-modal-sub">
        Earn half of the subscription income from every recruit,
        for a whole year from their sign-up.
      </p>
      <CopyRow value={link('cash')} label="referral link" />
      <div className="affil-stats">
        <div className="affil-stat"><span className="affil-stat-num">{stats.signups}</span><span className="affil-stat-label">Sign-ups</span></div>
        <div className="affil-stat"><span className="affil-stat-num">{stats.paid}</span><span className="affil-stat-label">Subscribed</span></div>
        <div className="affil-stat"><span className="affil-stat-num">{money(stats.pendingCents)}</span><span className="affil-stat-label">Pending</span></div>
        <div className="affil-stat"><span className="affil-stat-num">{money(stats.paidOutCents)}</span><span className="affil-stat-label">Paid</span></div>
      </div>
      {stage === 'locked' ? (
        <button className="gift-modal-submit affil-connect" onClick={connect} disabled={connecting}>
          <Landmark size={15} />
          {connecting ? 'Opening Stripe…' : 'Connect Stripe to get paid'}
        </button>
      ) : (
        <p className="affil-fine">Paid monthly by Stripe after each payment clears its 30-day refund window.</p>
      )}
    </>
  );
}

export default function ReferralPanel({ user, preview }: ReferralPanelProps) {
  const [open, setOpen] = useState(false);

  if (preview) {
    return (
      <div className="gift-modal referral-modal">
        <AffiliateBody user={user} preview={preview} />
      </div>
    );
  }

  return (
    <>
      {/* The foot of Home: a banner naming the program, the whole thing the
          way in. */}
      <button className="referral-banner" onClick={() => setOpen(true)}>
        <span className="referral-banner-icon"><HandCoins size={26} /></span>
        <span className="referral-banner-text">
          <span className="referral-banner-head">Invite others and get paid</span>
          <span className="referral-banner-sub">Join our Affiliate Program</span>
        </span>
      </button>

      {open && (
        <div className="modal-backdrop" onClick={() => setOpen(false)}>
          <div className="gift-modal referral-modal" onClick={(e) => e.stopPropagation()}>
            <button className="gift-modal-close" onClick={() => setOpen(false)} aria-label="Close">
              <X size={18} />
            </button>
            <AffiliateBody user={user} />
          </div>
        </div>
      )}
    </>
  );
}
